/**
 * Hyperlocal / city market SEO landings for serviceapp.ai.
 * Each page must carry unique local substance — not industry copy with a city name swapped in.
 */

export interface CitySeoPage {
  slug: string;
  city: string;
  region: string;
  country: string;
  countryCode: string;
  currency: string;
  timezone: string;
  keyword: string;
  title: string;
  description: string;
  keywords: string;
  h1: string;
  h1Accent: string;
  subheadline: string;
  /** Unique local operating context (tax, messaging norms, labour). */
  localContext: string;
  localNorms: Array<{ title: string; body: string }>;
  scenarios: Array<{ title: string; body: string }>;
  heroImage: string;
  heroImageAlt: string;
  faqs: Array<{ q: string; a: string }>;
  relatedIndustrySlugs: string[];
  /** Sitemap lastmod. Omit to keep the stable corpus date. */
  updated?: string;
}

const BASE =
  'ServiceApp, ServiceApp.ai, field service software, service business software, WhatsApp quoting, job scheduling, crew dispatch, invoicing';

export const CITY_SEO_PAGES: CitySeoPage[] = [
  {
    slug: '/dubai-field-service-software',
    city: 'Dubai',
    region: 'Dubai',
    country: 'United Arab Emirates',
    countryCode: 'AE',
    currency: 'AED',
    timezone: 'Asia/Dubai',
    keyword: 'Dubai field service software',
    title: 'Dubai Field Service Software | WhatsApp Quotes & VAT — ServiceApp.ai',
    description:
      'Field service software for Dubai cleaning, HVAC, pest and fit-out teams. WhatsApp quotes in AED, VAT-ready invoices, crew dispatch and payments — built for UAE service businesses.',
    keywords: `Dubai field service software, Dubai cleaning software, UAE service business app, Dubai job scheduling, VAT invoicing Dubai, ${BASE}`,
    h1: 'Field service software for Dubai',
    h1Accent: 'built for AED, VAT and WhatsApp.',
    subheadline:
      'Dubai crews win work on WhatsApp and lose it in spreadsheets. ServiceApp keeps enquiries, AED quotes, crew schedules and VAT invoices in one app.',
    localContext:
      'Dubai service businesses often mix villa AMC contracts, hotel FM work and fit-out snag lists — with customers who expect instant WhatsApp replies and Arabic/English paperwork. ServiceApp configures UAE VAT, AED pricing and Gulf phone formats during setup so office and field stay aligned across Business Bay, JLT, Marina and Dubai Hills routes.',
    localNorms: [
      {
        title: 'UAE VAT on every invoice',
        body: 'Apply 5% VAT (or your configured rate) on quotes and tax invoices that match how Dubai authorities expect line items and TRN fields.',
      },
      {
        title: 'WhatsApp-first sales',
        body: 'Send branded quotations customers open on the phone — the channel Dubai owners already use to close cleaning, AC and pest jobs.',
      },
      {
        title: 'Multi-crew Gulf hours',
        body: 'Schedule Saturday–Thursday patterns, split day/night HVAC shifts and keep supervisors on the same calendar as office staff.',
      },
    ],
    scenarios: [
      {
        title: 'Villa deep-clean + AMC',
        body: 'A Jumeirah villa enquiry becomes an AED WhatsApp quote with add-ons, then a recurring monthly booking with checklist evidence for the FM desk.',
      },
      {
        title: 'AC emergency in Marina',
        body: 'Dispatch the nearest tech, capture before/after photos on site, and collect card or link payment before leaving the tower.',
      },
    ],
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'Dubai service business owner managing quotes and crews in ServiceApp',
    faqs: [
      {
        q: 'Is ServiceApp suitable for Dubai service companies?',
        a: 'Yes. Dubai teams use ServiceApp for cleaning, HVAC, pest, plumbing and interiors — with AED display pricing, UAE VAT fields and WhatsApp quoting built for how Gulf customers buy.',
      },
      {
        q: 'Can invoices include UAE VAT and TRN?',
        a: 'Yes. Configure your tax system and company tax ID during setup so quotes and invoices show VAT correctly for UAE customers.',
      },
      {
        q: 'Do field staff need an app store download?',
        a: 'No. Supervisors and technicians open ServiceApp on any phone browser — routes, ETAs, checklists and collections without a separate install.',
      },
    ],
    relatedIndustrySlugs: [
      '/cleaning-service-software',
      '/hvac-service-software',
      '/interior-fitout-software',
      '/field-service-software',
    ],
  },
  {
    slug: '/abu-dhabi-field-service-software',
    city: 'Abu Dhabi',
    region: 'Abu Dhabi',
    country: 'United Arab Emirates',
    countryCode: 'AE',
    currency: 'AED',
    timezone: 'Asia/Dubai',
    keyword: 'Abu Dhabi field service software',
    title: 'Abu Dhabi Field Service Software | Dispatch & VAT — ServiceApp.ai',
    description:
      'Run Abu Dhabi field service teams with AED WhatsApp quotes, government and villa contracts, VAT invoices and mobile crew apps. Free trial from ServiceApp.ai.',
    keywords: `Abu Dhabi field service software, Abu Dhabi cleaning software, UAE FM software, Al Ain dispatch software, ${BASE}`,
    h1: 'Abu Dhabi field service software',
    h1Accent: 'for contracts that span the capital.',
    subheadline:
      'From Corniche villas to Saadiyat and Mussafah industrial sites — keep quotes, permits paperwork and crew proof in one system.',
    localContext:
      'Abu Dhabi operators often balance long government or developer SLAs with residential AMC routes toward Al Ain. Teams need bilingual customer communication, strict job evidence and VAT invoices in AED. ServiceApp keeps enquiry → quote → visit → invoice on one timeline so office staff are not chasing WhatsApp threads for each island and industrial zone.',
    localNorms: [
      {
        title: 'Evidence-first jobs',
        body: 'Photo checklists and completion notes support SLA reviews common on Abu Dhabi facility and villa contracts.',
      },
      {
        title: 'AED + UAE tax setup',
        body: 'Price in dirhams and apply VAT rules once at org level — every quote and invoice inherits the same configuration.',
      },
      {
        title: 'Long-distance routes',
        body: 'Plan capital-city and Al Ain days on one calendar so crews are not double-booked across emirates.',
      },
    ],
    scenarios: [
      {
        title: 'Developer handover snags',
        body: 'Fit-out punch lists become scheduled visits with photo evidence and milestone invoices for the main contractor.',
      },
      {
        title: 'Pest route across compounds',
        body: 'Recurring treatments across Khalifa City compounds auto-schedule with reminders and consolidated monthly billing.',
      },
    ],
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'Abu Dhabi field service owner reviewing schedules in ServiceApp',
    faqs: [
      {
        q: 'Does ServiceApp work for Abu Dhabi FM and residential teams?',
        a: 'Yes. Configure service types for villas, compounds and commercial sites, then dispatch crews with mobile checklists and AED invoicing.',
      },
      {
        q: 'Can we run Al Ain jobs from an Abu Dhabi office?',
        a: 'Yes. One workspace calendar covers multiple cities — assign teams by zone and keep every job’s paperwork in the same account.',
      },
    ],
    relatedIndustrySlugs: [
      '/field-service-software',
      '/pest-control-software',
      '/cleaning-service-software',
      '/interior-fitout-software',
    ],
  },
  {
    slug: '/riyadh-field-service-software',
    city: 'Riyadh',
    region: 'Riyadh',
    country: 'Saudi Arabia',
    countryCode: 'SA',
    currency: 'SAR',
    timezone: 'Asia/Riyadh',
    keyword: 'Riyadh field service software',
    title: 'Riyadh Field Service Software | SAR Quotes & ZATCA-Ready Ops — ServiceApp.ai',
    description:
      'Field service software for Riyadh contractors. SAR pricing, WhatsApp quotes, crew dispatch and tax-ready invoices for cleaning, HVAC, pest and fit-out teams in KSA.',
    keywords: `Riyadh field service software, Saudi service business software, SAR quoting software, Riyadh cleaning HVAC app, ${BASE}`,
    h1: 'Field service software for Riyadh',
    h1Accent: 'SAR quotes, crews and cash.',
    subheadline:
      'Riyadh’s growth markets — compounds, malls and fit-out — need faster quoting and tighter cash collection. ServiceApp is the ops layer.',
    localContext:
      'Riyadh service companies sell heavily over WhatsApp and still reconcile cash in Excel across North, East and diplomatic-quarter routes. Owners need SAR price books, Arabic-friendly customer messaging and invoices that match KSA tax expectations. ServiceApp centralises enquiries, job proof and collections so expansion to Jeddah or Dammam does not mean a second spreadsheet empire.',
    localNorms: [
      {
        title: 'SAR catalogue & tax fields',
        body: 'Set Saudi Riyal pricing and company tax identifiers once — quotes and invoices stay consistent as you scale crews.',
      },
      {
        title: 'WhatsApp close rates',
        body: 'Send structured quotations with scope and add-ons instead of fragmented chat price lists that never become booked jobs.',
      },
      {
        title: 'Ramadan & shift flexibility',
        body: 'Adjust opening hours and crew capacity without rebuilding your whole schedule tool every season.',
      },
    ],
    scenarios: [
      {
        title: 'Compound cleaning contract',
        body: 'A North Riyadh compound tender becomes recurring jobs with crew checklists and a monthly SAR invoice pack.',
      },
      {
        title: 'HVAC AMC renewal',
        body: 'Track servicing visits, capture refrigerant/checklist notes and auto-prompt renewals before contracts lapse.',
      },
    ],
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'Riyadh service company owner using ServiceApp for SAR quotes and dispatch',
    faqs: [
      {
        q: 'Can ServiceApp price and invoice in Saudi Riyal?',
        a: 'Yes. Choose Saudi Arabia during setup to apply SAR, local phone formats and tax fields. Marketing pages can also show SAR estimates for visitors.',
      },
      {
        q: 'Is it only for Riyadh or all of KSA?',
        a: 'One workspace can run Riyadh, Jeddah and Eastern Province teams — city pages help local SEO, the product is national.',
      },
    ],
    relatedIndustrySlugs: [
      '/hvac-service-software',
      '/cleaning-service-software',
      '/field-service-software',
      '/interior-fitout-software',
    ],
  },
  {
    slug: '/mumbai-field-service-software',
    city: 'Mumbai',
    region: 'Maharashtra',
    country: 'India',
    countryCode: 'IN',
    currency: 'INR',
    timezone: 'Asia/Kolkata',
    keyword: 'Mumbai field service software',
    title: 'Mumbai Field Service Software | GST, WhatsApp & Crew Apps — ServiceApp.ai',
    description:
      'Field service software for Mumbai service businesses. INR pricing, GST invoices, WhatsApp quotes and mobile dispatch for cleaning, AC, pest and facility teams.',
    keywords: `Mumbai field service software, Mumbai cleaning software, GST service business app, Mumbai job scheduling, ${BASE}`,
    h1: 'Mumbai field service software',
    h1Accent: 'GST-ready and WhatsApp-native.',
    subheadline:
      'From Andheri AMC routes to South Mumbai commercial contracts — quote in INR, schedule crews and raise GST invoices without Excel.',
    localContext:
      'Mumbai operators juggle society AMCs, corporate facilities and rain-season spikes while customers expect WhatsApp confirmations in minutes. GSTIN on invoices, pin-code routes across Western and Harbour lines, and cash-vs-UPI collections are daily realities. ServiceApp is configured for Indian GST and INR so office staff stop retyping the same job into three tools.',
    localNorms: [
      {
        title: 'GST invoices',
        body: 'Configure GST rates and GSTIN so every completed job can become a compliant tax invoice with payment links.',
      },
      {
        title: 'Dense urban routing',
        body: 'Keep society and office towers on one calendar so Bandra and Navi Mumbai crews are not double-booked in peak hours.',
      },
      {
        title: 'UPI-friendly collections',
        body: 'Send payment links the moment the job is marked complete — fewer end-of-month chase calls.',
      },
    ],
    scenarios: [
      {
        title: 'Society deep-clean weekend',
        body: 'Batch WhatsApp quotes for multiple wings, book Saturday slots and capture checklist photos for the society manager.',
      },
      {
        title: 'AC gas-top-up rush',
        body: 'Summer peaks become dispatchable jobs with parts notes and same-day INR invoices.',
      },
    ],
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'Mumbai service business owner managing GST invoices in ServiceApp',
    faqs: [
      {
        q: 'Does ServiceApp support Indian GST?',
        a: 'Yes. Select India during setup to apply GST, INR and local phone formats. Tax reports stay available from your workspace.',
      },
      {
        q: 'Can technicians use it on Android phones?',
        a: 'Yes. Field apps run in the mobile browser — no Play Store dependency for supervisors or cleaners.',
      },
    ],
    relatedIndustrySlugs: [
      '/cleaning-service-software',
      '/hvac-service-software',
      '/pest-control-software',
      '/field-service-software',
    ],
  },
  {
    slug: '/delhi-field-service-software',
    city: 'Delhi',
    region: 'Delhi NCR',
    country: 'India',
    countryCode: 'IN',
    currency: 'INR',
    timezone: 'Asia/Kolkata',
    keyword: 'Delhi field service software',
    title: 'Delhi NCR Field Service Software | GST & Dispatch — ServiceApp.ai',
    description:
      'Field service software for Delhi NCR. Run Gurgaon, Noida and Delhi crews with INR WhatsApp quotes, GST invoices and mobile job reports.',
    keywords: `Delhi field service software, Gurgaon cleaning software, Noida HVAC software, Delhi NCR service app, ${BASE}`,
    h1: 'Delhi NCR field service software',
    h1Accent: 'for multi-city crews.',
    subheadline:
      'One workspace for Delhi, Gurugram and Noida — enquiries, GST quotes and cross-city dispatch without three WhatsApp groups.',
    localContext:
      'NCR businesses sell across state borders in a single day: a Noida corporate clean in the morning and a South Delhi villa pest visit after lunch. That creates GST complexity, traffic-aware scheduling and multi-warehouse parts needs. ServiceApp keeps one customer and job record so finance is not reconciling three city spreadsheets every Friday.',
    localNorms: [
      {
        title: 'Cross-city operations',
        body: 'Assign teams by zone (Delhi / Gurugram / Noida) while leadership sees one live pipeline and calendar.',
      },
      {
        title: 'GST for service work',
        body: 'Apply your GST configuration on quotes and invoices so NCR B2B customers get clean tax documents.',
      },
      {
        title: 'Winter AMC surge',
        body: 'Scale HVAC and cleaning capacity with office-user plans — unlimited seats as you add seasonal staff.',
      },
    ],
    scenarios: [
      {
        title: 'Corporate FM day shift',
        body: 'Recurring office cleans across Cyber City towers with supervisor checklists and consolidated billing.',
      },
      {
        title: 'Pest monsoon prep',
        body: 'Book treatment packages for societies before peak season with automated reminders and INR invoices.',
      },
    ],
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'Delhi NCR service operator dispatching crews in ServiceApp',
    faqs: [
      {
        q: 'Can one ServiceApp account cover Delhi, Gurgaon and Noida?',
        a: 'Yes. Use a single organisation with teams and service areas — city landing pages are for SEO; the product is multi-city.',
      },
      {
        q: 'How does pricing work for Indian teams?',
        a: 'Plans charge for office users. Unlimited crew. Visitors in India can see INR display estimates on the marketing site.',
      },
    ],
    relatedIndustrySlugs: [
      '/field-service-software',
      '/cleaning-service-software',
      '/pest-control-software',
      '/hvac-service-software',
    ],
  },
  {
    slug: '/bangalore-field-service-software',
    city: 'Bangalore',
    region: 'Karnataka',
    country: 'India',
    countryCode: 'IN',
    currency: 'INR',
    timezone: 'Asia/Kolkata',
    keyword: 'Bangalore field service software',
    title: 'Bangalore Field Service Software | Tech-Park & Home Services — ServiceApp.ai',
    description:
      'Field service software for Bangalore / Bengaluru teams. INR + GST, WhatsApp quotes and dispatch for apartment, villa and tech-park service businesses.',
    keywords: `Bangalore field service software, Bengaluru cleaning software, Bangalore HVAC software, Koramangala service app, ${BASE}`,
    h1: 'Bangalore field service software',
    h1Accent: 'for apartments, villas and tech parks.',
    subheadline:
      'Whitefield to Electronic City — close WhatsApp jobs faster and keep GST paperwork ready for apartment and corporate clients.',
    localContext:
      'Bengaluru service demand clusters around tech parks, gated communities and interior fit-out snags after handover. Customers expect English WhatsApp updates and digital invoices; crews need clear pin locations across Outer Ring Road traffic. ServiceApp gives owners a SaaS ops stack that matches how Bangalore already buys services — mobile-first, GST-aware and audit-friendly for facilities managers.',
    localNorms: [
      {
        title: 'Apartment AMC packs',
        body: 'Productise deep-clean, AC and pest packages for societies with recurring schedules and shared checklists.',
      },
      {
        title: 'Tech-park SLAs',
        body: 'Capture on-site evidence facilities managers ask for — photos, timestamps and signed completion notes.',
      },
      {
        title: 'INR + GST defaults',
        body: 'India locale setup applies currency and tax labels so quotes look native to Bangalore B2B buyers.',
      },
    ],
    scenarios: [
      {
        title: 'Handover snag list',
        body: 'Interior fit-out punch items become sequenced jobs with photo proof for the builder and homeowner.',
      },
      {
        title: 'Weekend AC overload',
        body: 'Overflow bookings auto-queue to available techs with live status for customer WhatsApp follow-ups.',
      },
    ],
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'Bangalore service business managing apartment AMCs in ServiceApp',
    faqs: [
      {
        q: 'Is ServiceApp used for Bangalore apartment and corporate work?',
        a: 'Yes. Configure residential and commercial service types, then schedule crews across Indiranagar, Whitefield, Sarjapur and beyond.',
      },
      {
        q: 'Do we need separate software for GST?',
        a: 'Tax configuration lives inside ServiceApp. Export or report from the workspace instead of re-entering jobs into a second GST tool.',
      },
    ],
    relatedIndustrySlugs: [
      '/interior-fitout-software',
      '/hvac-service-software',
      '/cleaning-service-software',
      '/field-service-software',
    ],
  },
  {
    slug: '/kochi-field-service-software',
    city: 'Kochi',
    region: 'Kerala',
    country: 'India',
    countryCode: 'IN',
    currency: 'INR',
    timezone: 'Asia/Kolkata',
    keyword: 'Kochi field service software',
    title: 'Kochi Field Service Software | Kerala Service Businesses — ServiceApp.ai',
    description:
      'Field service software for Kochi and Kerala teams. WhatsApp quotes in INR, GST invoices, crew scheduling for cleaning, AC, pest and facility services.',
    keywords: `Kochi field service software, Kerala cleaning software, Ernakulam service business app, Kochi HVAC software, ${BASE}`,
    h1: 'Kochi field service software',
    h1Accent: 'for Kerala service owners.',
    subheadline:
      'Run Ernakulam, Vytilla and Infopark routes with WhatsApp quotes, GST billing and crews who actually see today’s jobs.',
    localContext:
      'Kochi operators serve a mix of Gulf-return home renovations, apartment complexes and Infopark facilities — often with Malayalam + English customer chats. Monsoon seasons spike pest and waterproofing demand while AC AMCs peak in summer. ServiceApp replaces notebook + WhatsApp ops with one GST-ready system that still feels natural for small Kerala crews.',
    localNorms: [
      {
        title: 'Bilingual customer reality',
        body: 'Keep structured English quotes and invoices while your team continues WhatsApp conversations customers prefer.',
      },
      {
        title: 'Monsoon & festival seasons',
        body: 'Scale bookings and reminders when pest, cleaning and repair demand spikes — without hiring an office clerk.',
      },
      {
        title: 'Small-crew unlimited seats',
        body: 'Add helpers during peak months without per-seat fees eating Kerala margins.',
      },
    ],
    scenarios: [
      {
        title: 'Infopark facility clean',
        body: 'Recurring office contracts with supervisor checklists and monthly GST invoices for the facility manager.',
      },
      {
        title: 'Villa waterproofing follow-up',
        body: 'Quote, schedule revisit windows and attach photo evidence before collecting the final INR payment.',
      },
    ],
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'Kochi service business owner using ServiceApp for WhatsApp quotes',
    faqs: [
      {
        q: 'Is ServiceApp a fit for small Kochi teams?',
        a: 'Yes. Start and Grow are built for owner-operators and small crews — unlimited crew, GST and WhatsApp quoting included.',
      },
      {
        q: 'Can we expand to Thrissur or Trivandrum later?',
        a: 'Yes. Keep one organisation and add teams or service areas as you grow across Kerala.',
      },
    ],
    relatedIndustrySlugs: [
      '/cleaning-service-software',
      '/pest-control-software',
      '/hvac-service-software',
      '/field-service-software',
    ],
  },
  {
    slug: '/london-field-service-software',
    city: 'London',
    region: 'England',
    country: 'United Kingdom',
    countryCode: 'GB',
    currency: 'GBP',
    timezone: 'Europe/London',
    keyword: 'London field service software',
    title: 'London Field Service Software | VAT Quotes & Dispatch — ServiceApp.ai',
    description:
      'Field service software for London trades. GBP pricing, VAT invoices, WhatsApp/email quotes and crew dispatch for cleaning, HVAC, pest and property services.',
    keywords: `London field service software, UK cleaning software, London job scheduling software, VAT field service app, ${BASE}`,
    h1: 'London field service software',
    h1Accent: 'VAT-ready ops for UK crews.',
    subheadline:
      'Serve Zones 1–6 without drowning in WhatsApps — quote in GBP, schedule vans and invoice with VAT in one system.',
    localContext:
      'London service firms balance Congestion Charge routing, multi-borough contracts and customers who expect email + WhatsApp updates. CIS and VAT paperwork still collide with day-to-day dispatch. ServiceApp gives UK locale defaults (GBP, VAT labelling, phone formats) so office managers stop copying jobs from chat into Xero-only workflows.',
    localNorms: [
      {
        title: 'GBP + VAT invoices',
        body: 'Configure UK tax so quotes and invoices show VAT correctly for domestic customers.',
      },
      {
        title: 'Borough-dense calendars',
        body: 'Keep cleaners and engineers visible across North, South and East London without double-booking vans.',
      },
      {
        title: 'Evidence for property managers',
        body: 'Photo checklists and completion notes help close tickets for agencies and FM desks.',
      },
    ],
    scenarios: [
      {
        title: 'Multi-let block cleans',
        body: 'Recurring communal cleans with shared checklists and monthly consolidated billing per estate.',
      },
      {
        title: 'Boiler call-out day',
        body: 'Emergency slots, on-site reports and card/link collection before the engineer leaves the flat.',
      },
    ],
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'London field service company scheduling jobs in ServiceApp',
    faqs: [
      {
        q: 'Does ServiceApp support UK VAT?',
        a: 'Yes. Choose United Kingdom during setup for GBP, VAT labels and local phone defaults.',
      },
      {
        q: 'Is this only for London?',
        a: 'The product works UK-wide. This page targets London search intent; the same workspace can run Manchester or Birmingham teams.',
      },
    ],
    relatedIndustrySlugs: [
      '/cleaning-service-software',
      '/hvac-service-software',
      '/field-service-software',
      '/plumbing-business-software',
    ],
  },
  {
    slug: '/new-york-field-service-software',
    city: 'New York',
    region: 'New York',
    country: 'United States',
    countryCode: 'US',
    currency: 'USD',
    timezone: 'America/New_York',
    keyword: 'New York field service software',
    title: 'New York Field Service Software | Scheduling & Invoicing — ServiceApp.ai',
    description:
      'Field service software for New York City teams. Quote, dispatch and invoice cleaning, HVAC, pest and handyman jobs across the five boroughs — free trial from ServiceApp.ai.',
    keywords: `New York field service software, NYC cleaning software, Brooklyn HVAC software, NYC job scheduling app, ${BASE}`,
    h1: 'New York field service software',
    h1Accent: 'for five-borough crews.',
    subheadline:
      'Manhattan to Queens — capture leads, schedule techs and get paid without a stack of disconnected apps.',
    localContext:
      'NYC operators deal with elevator buildings, DOB-aware access windows and customers who text first. Sales tax rules and borough travel time make “just use a calendar” fail quickly. ServiceApp is a modern SaaS ops layer: enquiries, quotes, dispatch, job evidence and payments — with unlimited seats when you staff up for winter HVAC or spring cleaning peaks.',
    localNorms: [
      {
        title: 'Building access reality',
        body: 'Store entry notes, time windows and contact details on the job so techs are not stuck in lobbies.',
      },
      {
        title: 'USD catalogue + sales tax setup',
        body: 'Configure US locale and tax labels for the states you serve — start in NYC, expand without re-platforming.',
      },
      {
        title: 'Text-friendly quoting',
        body: 'Send clear digital quotes customers can accept — then convert straight into scheduled work.',
      },
    ],
    scenarios: [
      {
        title: 'Co-op corridor cleaning',
        body: 'Recurring building contracts with checklist proof for the board and monthly consolidated invoices.',
      },
      {
        title: 'Same-day handyman',
        body: 'Intake from a landing page, dispatch the nearest tech, collect payment before leaving the apartment.',
      },
    ],
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'New York field service owner dispatching borough crews in ServiceApp',
    faqs: [
      {
        q: 'Can ServiceApp run NYC cleaning and trade businesses?',
        a: 'Yes. It covers quote-to-cash for cleaning, HVAC, pest, plumbing and handyman teams with mobile field tools.',
      },
      {
        q: 'Is pricing per technician?',
        a: 'No. ServiceApp uses office-user plans with unlimited crew — better for seasonal NYC staffing.',
      },
    ],
    relatedIndustrySlugs: [
      '/field-service-software',
      '/cleaning-service-software',
      '/handyman-business-software',
      '/hvac-service-software',
    ],
  },
  {
    slug: '/sydney-field-service-software',
    city: 'Sydney',
    region: 'New South Wales',
    country: 'Australia',
    countryCode: 'AU',
    currency: 'AUD',
    timezone: 'Australia/Sydney',
    keyword: 'Sydney field service software',
    title: 'Sydney Field Service Software | GST Quotes & Dispatch — ServiceApp.ai',
    description:
      'Field service software for Sydney trades. AUD pricing, GST invoices, quotes and crew dispatch for cleaning, HVAC, pest and property services across Greater Sydney.',
    keywords: `Sydney field service software, Sydney cleaning software, Australia GST field service app, Parramatta job scheduling, ${BASE}`,
    h1: 'Sydney field service software',
    h1Accent: 'AUD quotes with GST built in.',
    subheadline:
      'North Shore to Western Sydney — run jobs, vans and GST invoices without living in WhatsApp and spreadsheets.',
    localContext:
      'Sydney service companies cover long coastal and western corridors with GST on almost every invoice and customers who expect SMS/WhatsApp ETAs. Strata managers want photo proof; homeowners want fast quotes. ServiceApp applies Australian defaults (AUD, GST labelling, phone formats) so owners get one app from first enquiry to payment instead of four partial tools.',
    localNorms: [
      {
        title: 'GST on quotes & invoices',
        body: 'Configure Australian GST so every completed job can invoice correctly for strata and residential clients.',
      },
      {
        title: 'Corridor scheduling',
        body: 'Plan North Shore vs Western Sydney days so vans are not crossing the Harbour Bridge twice unnecessarily.',
      },
      {
        title: 'Strata evidence packs',
        body: 'Checklists and photos close tickets for building managers without extra admin hours.',
      },
    ],
    scenarios: [
      {
        title: 'Strata common-area clean',
        body: 'Recurring cleans with shared checklists and monthly GST invoices per building.',
      },
      {
        title: 'Summer HVAC overload',
        body: 'Overflow bookings queue to available techs with on-site reports and payment links.',
      },
    ],
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'Sydney service business managing GST invoices in ServiceApp',
    faqs: [
      {
        q: 'Does ServiceApp support Australian GST?',
        a: 'Yes. Choose Australia during setup for AUD, GST labels and local phone defaults.',
      },
      {
        q: 'Can we run Brisbane or Melbourne from the same account?',
        a: 'Yes. Add teams and service areas as you expand — city pages are SEO entry points, not separate products.',
      },
    ],
    relatedIndustrySlugs: [
      '/cleaning-service-software',
      '/hvac-service-software',
      '/field-service-software',
      '/pest-control-software',
    ],
  },
  {
    slug: '/singapore-field-service-software',
    city: 'Singapore',
    region: 'Singapore',
    country: 'Singapore',
    countryCode: 'SG',
    currency: 'SGD',
    timezone: 'Asia/Singapore',
    keyword: 'Singapore field service software',
    title: 'Singapore Field Service Software | GST & Condo Ops — ServiceApp.ai',
    description:
      'Field service software for Singapore. SGD pricing, GST invoices, WhatsApp quotes and crew dispatch for cleaning, ACMV, pest and facility teams.',
    keywords: `Singapore field service software, Singapore cleaning software, SGD GST service app, condo facility software Singapore, ${BASE}`,
    h1: 'Singapore field service software',
    h1Accent: 'condo, office and ACMV ready.',
    subheadline:
      'HDB, condo and CBD contracts — WhatsApp quotes in SGD, GST invoices and crews who see the right job details.',
    localContext:
      'Singapore operators work in a dense, high-trust market: condo managing agents expect punctual ETAs, digital invoices and clear GST. WhatsApp is the sales channel; compliance and neat paperwork win renewals. ServiceApp configures SGD and GST labels during setup and keeps enquiry-to-payment evidence in one workspace — suited to cleaning, pest, ACMV and fit-out snag teams island-wide.',
    localNorms: [
      {
        title: 'SGD + GST invoices',
        body: 'Apply Singapore GST configuration so managing agents and SMEs get clean tax documents.',
      },
      {
        title: 'Condo access discipline',
        body: 'Store lobby instructions, loading-bay windows and MA contacts on every job card.',
      },
      {
        title: 'WhatsApp quoting',
        body: 'Send structured quotations that convert to bookings — not fragmented chat price lists.',
      },
    ],
    scenarios: [
      {
        title: 'Condo corridor + bin-centre pack',
        body: 'Recurring facility cleans with checklist proof for the managing agent and monthly GST billing.',
      },
      {
        title: 'ACMV preventive route',
        body: 'Schedule servicing across CBD floors with photo evidence and renewal reminders.',
      },
    ],
    heroImage: '/serviceapp-owner-cleaning.jpg',
    heroImageAlt: 'Singapore facility team coordinating jobs in ServiceApp',
    faqs: [
      {
        q: 'Is ServiceApp set up for Singapore GST?',
        a: 'Yes. Select Singapore during setup for SGD, GST labelling and local phone formats.',
      },
      {
        q: 'Does it work for condo and commercial FM?',
        a: 'Yes. Use recurring contracts, checklists and consolidated invoicing for managing agents and landlords.',
      },
    ],
    relatedIndustrySlugs: [
      '/cleaning-service-software',
      '/hvac-service-software',
      '/pest-control-software',
      '/field-service-software',
    ],
  },
];

export const CITY_SEO_SLUGS = CITY_SEO_PAGES.map((p) => p.slug);

export function getCitySeoPageByPath(pathname: string): CitySeoPage | undefined {
  const normalized = pathname.replace(/\/+$/, '') || '/';
  return CITY_SEO_PAGES.find((p) => p.slug === normalized);
}

export function getCitySeoPageBySlugParam(slug: string): CitySeoPage | undefined {
  const path = slug.startsWith('/') ? slug : `/${slug}`;
  return getCitySeoPageByPath(path);
}
